import type { Face } from '../mahjong-solitaire/tiles.js';
import { KIND_COUNT, faceOfKind, isDragon, isHonour, kindIndex, shanten, toCounts } from './tiles.js';
import type { MahjongView } from './view.js';

/**
 * `easy` discards a random tile, never claims a pung, kong or chow, and takes a win whenever it is offered one.
 * `clever` measures how far its hand is from complete (the usual "shanten" count) and discards the tile that keeps it closest, preferring
 * the one that leaves the most unseen tiles that would help; it claims a pung, kong or chow only when that brings the hand closer (or a pung of a
 * dragon or its own or the round wind, which scores), declares kongs, and always takes a win.
 */
export type MahjongRobotLevel = 'easy' | 'clever';

export type MahjongAction =
  | { readonly type: 'discard'; readonly tile: Face }
  | { readonly type: 'win' }
  | { readonly type: 'kong'; readonly tile?: Face }
  | { readonly type: 'pung' }
  | { readonly type: 'chow'; readonly tiles: readonly [Face, Face] }
  | { readonly type: 'pass' };

/** How many of each kind the viewer can see: its own hand, every discard, and every face-up meld. */
function seenCounts(view: MahjongView): number[] {
  const seen = toCounts(view.hand);
  const add = (f: Face) => {
    const i = kindIndex(f);
    if (i >= 0) seen[i] = (seen[i] as number) + 1;
  };
  for (const d of view.discards) add(d.tile);
  for (const melds of Object.values(view.melds)) for (const m of melds) for (const t of m.tiles) add(t);
  return seen;
}

/** The kinds a draw could help with: each kind in the hand and its neighbours in the same suit (honours only themselves). */
function relevantKinds(counts: readonly number[]): number[] {
  const out = new Set<number>();
  for (let i = 0; i < KIND_COUNT; i++) {
    if ((counts[i] as number) === 0) continue;
    out.add(i);
    if (i < 27) {
      const r = i % 9;
      for (const d of [-2, -1, 1, 2]) if (r + d >= 0 && r + d <= 8) out.add(i + d);
    }
  }
  return [...out];
}

/** The best shanten reachable by discarding one tile from `counts`. */
function bestAfterDiscard(counts: number[], exposed: number): number {
  let best = 8;
  for (let k = 0; k < KIND_COUNT; k++) {
    if ((counts[k] as number) === 0) continue;
    counts[k] = (counts[k] as number) - 1;
    best = Math.min(best, shanten(counts, exposed));
    counts[k] = (counts[k] as number) + 1;
  }
  return best;
}

function cleverDiscard(view: MahjongView): Face {
  const counts = toCounts(view.hand);
  const exposed = view.melds[view.viewer].length;
  const seen = seenCounts(view);
  const scored: { k: number; s: number }[] = [];
  for (let k = 0; k < KIND_COUNT; k++) {
    if ((counts[k] as number) === 0) continue;
    counts[k] = (counts[k] as number) - 1;
    scored.push({ k, s: shanten(counts, exposed) });
    counts[k] = (counts[k] as number) + 1;
  }
  const min = Math.min(...scored.map((x) => x.s));
  const cands = scored.filter((x) => x.s === min);
  let pick = cands[0] as { k: number; s: number };
  if (cands.length > 1) {
    // More helpful tiles left first; then the tile already most seen (safer to throw); then an honour; then the higher index.
    const keyOf = (c: { k: number }): number[] => {
      counts[c.k] = (counts[c.k] as number) - 1;
      let useful = 0;
      for (const d of relevantKinds(counts)) {
        const unseen = Math.max(0, 4 - (seen[d] as number));
        if (unseen === 0) continue;
        counts[d] = (counts[d] as number) + 1;
        if (shanten(counts, exposed) < min) useful += unseen;
        counts[d] = (counts[d] as number) - 1;
      }
      counts[c.k] = (counts[c.k] as number) + 1;
      return [useful, seen[c.k] as number, isHonour(faceOfKind(c.k)) ? 1 : 0, c.k];
    };
    const keyed = cands.map((c) => ({ c, key: keyOf(c) }));
    keyed.sort((x, y) => {
      for (let i = 0; i < 4; i++) if (x.key[i] !== y.key[i]) return (y.key[i] as number) - (x.key[i] as number);
      return 0;
    });
    pick = (keyed[0] as { c: { k: number; s: number } }).c;
  }
  return faceOfKind(pick.k);
}

/** The shanten after claiming (removing `take` tiles from the hand, adding one meld) and then discarding the best tile. */
function afterClaim(view: MahjongView, take: readonly Face[]): number {
  const counts = toCounts(view.hand);
  for (const f of take) {
    const i = kindIndex(f);
    counts[i] = (counts[i] as number) - 1;
  }
  return bestAfterDiscard(counts, view.melds[view.viewer].length + 1);
}

/** Is a pung of this tile worth having for its own sake: a dragon, the viewer's seat wind, or the round wind. */
const scoringPung = (view: MahjongView, tile: Face): boolean => isDragon(tile) || tile === `wind-${view.seatWinds[view.viewer]}` || tile === `wind-${view.roundWind}`;

function cleverClaim(view: MahjongView): MahjongAction {
  const claim = view.claim;
  const pending = view.pending;
  if (!claim || !pending) return { type: 'pass' };
  if (claim.win) return { type: 'win' };
  const hand = view.hand;
  const t = pending.tile;
  const before = shanten(toCounts(hand), view.melds[view.viewer].length);
  if (claim.kong) {
    const s = afterClaim(view, [t, t, t]);
    if (s < before || (s === before && scoringPung(view, t))) return { type: 'kong' };
  }
  if (claim.pung) {
    const s = afterClaim(view, [t, t]);
    if (s < before || (s === before && scoringPung(view, t))) return { type: 'pung' };
  }
  let best: { tiles: readonly [Face, Face]; s: number } | null = null;
  for (const pair of claim.chows) {
    const s = afterClaim(view, pair);
    if (s < before && (!best || s < best.s)) best = { tiles: pair, s };
  }
  return best ? { type: 'chow', tiles: best.tiles } : { type: 'pass' };
}

/**
 * What a robot does when it must act: it sees only its own `MahjongView`. `random` is used by the easy robot only, so a clever game is fully
 * deterministic. One action per call; the caller asks again until the hand ends.
 */
export function mahjongChoose(view: MahjongView, level: MahjongRobotLevel, random: () => number): MahjongAction {
  if (!view.canAct) throw new Error("mahjongChoose: not this seat's turn");
  if (view.phase === 'claim') {
    if (level === 'easy') return view.claim?.win ? { type: 'win' } : { type: 'pass' };
    return cleverClaim(view);
  }
  if (view.canWin) return { type: 'win' };
  if (level === 'clever' && view.kongTiles.length > 0) return { type: 'kong', tile: view.kongTiles[0] as Face };
  if (level === 'easy') return { type: 'discard', tile: view.discardable[Math.floor(random() * view.discardable.length)] as Face };
  return { type: 'discard', tile: cleverDiscard(view) };
}

/** A suggestion for a human: the clever robot's choice for this view. It reads only the viewer's own view, so it can never leak a hidden tile. */
export const mahjongHint = (view: MahjongView): MahjongAction => mahjongChoose(view, 'clever', () => 0);
